import { defineComponent as _defineComponent } from 'vue';
import { BAlert } from "bootstrap-vue";
import { storeToRefs } from "pinia";
import { computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router/composables";
import { useToolStore } from "@/stores/toolStore";
import { useUserStore } from "@/stores/userStore";
import Filtering, { contains } from "@/utils/filtering";
import DelayedInput from "@/components/Common/DelayedInput.vue";
import FilterMenu from "@/components/Common/FilterMenu.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'ToolSearch',
    props: {
        currentPanelView: {
            type: String,
            required: true,
        },
        enableAdvanced: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: "search tools",
        },
        query: {
            type: String,
            default: null,
        },
        queryPending: {
            type: Boolean,
            default: false,
        },
        showAdvanced: {
            type: Boolean,
            default: false,
        },
        toolsList: {
            type: Array,
            required: true,
        },
        currentPanel: {
            type: Object,
            required: true,
        },
    },
    emits: ["update:show-advanced", "onResults", "onQuery"],
    setup(__props, { emit }) {
        const props = __props;
        const router = useRouter();
        // Note: These are ordered by result priority (exact matches very top; words matches very bottom)
        const KEYS = { exact: 5, startsWith: 4, name: 3, description: 2, combined: 1, wordMatch: 0 };
        const FAVORITES = ["#favs", "#favorites", "#favourites"];
        const MIN_QUERY_LENGTH = 3;
        const localFilterText = computed({
            get: () => {
                return props.query !== null ? props.query : "";
            },
            set: (newVal) => {
                checkQuery(newVal);
            },
        });
        const propShowAdvanced = computed({
            get: () => {
                return props.showAdvanced;
            },
            set: (val) => {
                emit("update:show-advanced", val);
            },
        });
        const validFilters = computed(() => {
            return {
                name: { placeholder: "name", type: String, handler: contains("name"), menuItem: true },
                section: {
                    placeholder: "section",
                    type: String,
                    handler: contains("section"),
                    datalist: sectionNames,
                    menuItem: true,
                },
                ontology: {
                    placeholder: "EDAM ontology",
                    type: String,
                    handler: contains("ontology"),
                    datalist: ontologyList.value,
                    menuItem: true,
                },
                id: { placeholder: "id", type: String, handler: contains("id"), menuItem: true },
                owner: { placeholder: "repository owner", type: String, handler: contains("owner"), menuItem: true },
                help: { placeholder: "help text", type: String, handler: contains("help"), menuItem: true },
            };
        });
        const ToolFilters = computed(() => new Filtering(validFilters.value));
        const { currentFavorites } = storeToRefs(useUserStore());
        const toolStore = useToolStore();
        const { searchWorker } = storeToRefs(toolStore);
        const sectionNames = toolStore.sectionDatalist("default").map((option) => option.text);
        const ontologyList = computed(() => toolStore.sectionDatalist("ontology:edam_topics").concat(toolStore.sectionDatalist("ontology:edam_operations")));
        onMounted(() => {
            // initialize worker
            if (!searchWorker.value) {
                searchWorker.value = new Worker(new URL("components/Panels/toolSearch.worker.js", import.meta.url));
            }
            searchWorker.value.onmessage = ({ data }) => {
                const { type, payload, sectioned, query, closestTerm } = data;
                if (type === "searchToolsByKeysResult" && query === props.query) {
                    emit("onResults", payload, sectioned, closestTerm);
                }
                else if (type === "clearFilterResult") {
                    emit("onResults", null, null, null);
                }
                else if (type === "favoriteToolsResult") {
                    emit("onResults", currentFavorites.value.tools, null, null);
                }
            };
        });
        onUnmounted(() => {
            var _a;
            // The worker is not terminated but it will not be listening to messages
            if ((_a = searchWorker.value) === null || _a === void 0 ? void 0 : _a.onmessage) {
                searchWorker.value.onmessage = null;
            }
        });
        watch(() => currentFavorites.value.tools, () => {
            if (FAVORITES.includes(props.query)) {
                post({ type: "favoriteTools" });
            }
        });
        function checkQuery(q) {
            emit("onQuery", q);
            if (q.trim() && q.trim().length >= MIN_QUERY_LENGTH) {
                if (FAVORITES.includes(q)) {
                    post({ type: "favoriteTools" });
                }
                else {
                    post({
                        type: "searchToolsByKeys",
                        payload: {
                            tools: props.toolsList,
                            keys: KEYS,
                            query: q,
                            panelView: props.currentPanelView,
                            currentPanel: props.currentPanel,
                        },
                    });
                }
            }
            else {
                post({ type: "clearFilter" });
            }
        }
        function post(message) {
            var _a;
            (_a = searchWorker.value) === null || _a === void 0 ? void 0 : _a.postMessage(message);
        }
        function onAdvancedSearch(filters) {
            router.push({ path: "/tools/list", query: filters });
        }
        return { __sfc: true, router, KEYS, FAVORITES, MIN_QUERY_LENGTH, props, emit, localFilterText, propShowAdvanced, validFilters, ToolFilters, currentFavorites, toolStore, searchWorker, sectionNames, ontologyList, checkQuery, post, onAdvancedSearch, BAlert, DelayedInput, FilterMenu, LoadingSpan };
    }
});
